/*
 * The POST /v2/sales_task_list/generation_config body's `rules` map, built from
 * the Task Generation card's state.
 *
 * Its own module, and pure, because this is where the config UI silently loses
 * fields: it builds a fresh literal per rule rather than sending the rule object
 * back, so a field added to the card and not added here is accepted by the
 * inputs, marked dirty, saved — and discarded, with no error anywhere. That is
 * exactly what the instant-tier fields would have hit, and it is why this is a
 * tested function rather than an inline loop in the page component.
 */

// A blank numeric rule field, as an integer or null. '' / null / undefined /
// unparseable all mean "the operator left the box empty"; 0 is a real value and
// survives (it disables the dedup window).
// A Task Generation card rule: goserver's RuleConfig JSON, with the numeric
// boxes holding whatever the input last produced.
type NumericInput = number | string | null | undefined;

export interface GenRule {
  task_type: string;
  enabled?: boolean;
  enabled_only?: boolean;
  is_instant?: boolean;
  expiry_minutes?: NumericInput;
  dedup_window_hours?: NumericInput;
  grace_minutes?: NumericInput;
  has_grace?: boolean;
  delay_minutes?: NumericInput;
  has_delay?: boolean;
  lookback_hours?: NumericInput;
  has_lookback?: boolean;
  days?: number[];
  has_days?: boolean;
  refresh_rate_hours?: NumericInput;
  due_offset_hours?: NumericInput;
  level?: string | null;
  routable?: boolean;
  priority?: string | null;
  call_window?: boolean | null;
  enabled_at?: string;
  // Client-only: tier fields the operator changed since the last load.
  tier_dirty?: TierField[];
}

export type TierField = 'level' | 'priority' | 'call_window';

// One entry of the POST body's `rules` map (goserver RuleConfig).
export interface GenRulePayload {
  enabled: boolean;
  is_instant: boolean;
  expiry_minutes?: number;
  dedup_window_hours?: number;
  grace_minutes?: number;
  delay_minutes?: number;
  lookback_hours?: number;
  days?: number[];
  refresh_rate_hours?: number;
  due_offset_hours?: number;
  level?: string;
  priority?: string;
  call_window?: boolean;
}

const optionalInt = (raw: NumericInput): number | null => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseInt(String(raw), 10);
  return Number.isNaN(n) ? null : n;
};

/*
 * Two omissions are deliberate, not oversights:
 *  - expiry_minutes is omitted when blank OR 0. The server reads an absent field
 *    as its 12 h default and clamps a present one up to its 1-minute minimum, so
 *    sending 0 would turn "default" into a one-minute expiry. (The GET emits 0
 *    for unset, which is why 0 must be treated as blank here.)
 *  - dedup_window_hours is omitted only when blank, so the server applies its own
 *    default; an explicit 0 is sent, because 0 means "no window" and must stay
 *    distinguishable from unset.
 *  - grace_minutes is sent only for the rules that declare has_grace, and only
 *    when the box is non-blank; an explicit 0 IS sent, because MinGraceMinutes
 *    is 0 and "create the task immediately" is a real setting. Sending it for a
 *    rule without has_grace would write a knob nothing reads; omitting it for a
 *    rule that has one wipes the stored value on every save.
 *  - delay_minutes is keyed on has_delay: it is the wait before the delayed
 *    re-check that decides whether the friction resolved itself. It is omitted
 *    when blank OR 0, like expiry and unlike grace: MinDelayMinutes is 1, so a
 *    0 is not rejected but clamped up to a one-minute delay — "no opinion" must
 *    round-trip as absent, letting the rule's own default apply. A server that
 *    does not send has_delay yet gets no key at all.
 *  - lookback_hours is keyed on has_lookback, on delay's terms: MinLookbackHours
 *    is 1, so blank and 0 are omitted and the rule's default applies.
 *
 *  - level, priority and call_window are sent only once the operator changed
 *    them (tier_dirty): the server keeps the stored value when a key is absent,
 *    and echoing an untouched value back would pin a catalogue default the
 *    server may later move. level '' = routed per student.
 *
 * enabled_at (server-stamped, read-only) is never sent.
 *
 * Enabled-only rules (Fresh lead, Upcoming trial session) still send no schedule
 * — an event or their own sweep is the trigger — but they DO send the
 * instant-tier fields: instant is orthogonal to the day-offset schedule, and
 * these event-driven rules are the ones most likely to be flipped instant.
 */
const buildGenConfigPayload = (
  genRules: Array<GenRule | null | undefined> | null | undefined
): Record<string, GenRulePayload> => {
  const rules: Record<string, GenRulePayload> = {};
  (genRules || []).forEach((r) => {
    if (!r || !r.task_type) return;
    const rule: GenRulePayload = { enabled: !!r.enabled, is_instant: !!r.is_instant };

    const expiry = optionalInt(r.expiry_minutes);
    if (expiry !== null && expiry !== 0) rule.expiry_minutes = expiry;

    const dedup = optionalInt(r.dedup_window_hours);
    if (dedup !== null) rule.dedup_window_hours = dedup;

    if (r.has_grace) {
      const grace = optionalInt(r.grace_minutes);
      if (grace !== null) rule.grace_minutes = grace;
    }

    if (r.has_delay) {
      const delay = optionalInt(r.delay_minutes);
      if (delay !== null && delay !== 0) rule.delay_minutes = delay;
    }

    if (r.has_lookback) {
      const lookback = optionalInt(r.lookback_hours);
      if (lookback !== null && lookback !== 0) rule.lookback_hours = lookback;
    }

    if (!r.enabled_only) {
      // has_days === false means the rule's generator never reads days (it has
      // its own re-scan window); absent means true, so nothing changes for a
      // server that does not send the flag yet.
      if (r.has_days !== false) rule.days = r.days || [];
      rule.refresh_rate_hours = Number(r.refresh_rate_hours) || 0;
      rule.due_offset_hours = Number(r.due_offset_hours) || 0;
    }

    const dirty = r.tier_dirty || [];
    if (dirty.includes('level') && typeof r.level === 'string') rule.level = r.level;
    if (dirty.includes('priority') && r.priority) rule.priority = r.priority;
    if (dirty.includes('call_window') && typeof r.call_window === 'boolean') {
      rule.call_window = r.call_window;
    }

    rules[r.task_type] = rule;
  });
  return rules;
};

// The rule after one tier edit, with the field marked dirty.
export const set_tier_field = (
  rule: GenRule,
  field: TierField,
  value: string | boolean
): GenRule => ({
  ...rule,
  [field]: value,
  tier_dirty: Array.from(new Set([...(rule.tier_dirty || []), field]))
});

export const DEFAULT_LEVELS = ['L1', 'L2', 'L3'];
export const DEFAULT_PRIORITIES = ['P0', 'P1', 'P2'];

// Level select options; "Routed" ('') only on a rule the server can route.
export const level_options = (
  rule: Pick<GenRule, 'routable' | 'level'>,
  levels: string[] | null | undefined
): Array<{ value: string; label: string }> => {
  const opts = (levels && levels.length ? levels : DEFAULT_LEVELS).map((l) => ({
    value: l,
    label: l
  }));
  return rule.routable ? [{ value: '', label: 'Routed' }, ...opts] : opts;
};

// ── Global settings ────────────────────────────────────────────────────────

export interface SkipReasonRow {
  code: string;
  label: string;
  // A row added in this session: its code follows the label. A stored code
  // never changes, because closed tasks already reference it.
  is_new?: boolean;
  // Client-only React key; never sent.
  id?: string;
}

export type GlobalsKey =
  | 'skip_reasons'
  | 'unclaimed_alert_buffer_minutes'
  | 'claim_idle_minutes'
  | 'call_window_start'
  | 'call_window_end'
  | 'unclaimed_alert_mentions'
  | 'unclaimed_alert_channel_id'
  | 'require_outcome'
  | 'duplicate_account_skip';

// The Global settings card's state.
export interface GenGlobals {
  skip_reasons: SkipReasonRow[];
  unclaimed_alert_buffer_minutes: { P0: NumericInput; P1: NumericInput; P2: NumericInput };
  claim_idle_minutes: NumericInput;
  call_window_start: string;
  call_window_end: string;
  // Comma-separated as typed.
  unclaimed_alert_mentions: string;
  unclaimed_alert_channel_id: string;
  require_outcome: boolean;
  duplicate_account_skip: boolean;
  dirty: GlobalsKey[];
}

export interface GlobalsBounds {
  min_claim_idle_minutes: number;
  max_claim_idle_minutes: number;
  min_unclaimed_buffer_minutes: number;
  max_unclaimed_buffer_minutes: number;
}

export const DEFAULT_GLOBALS_BOUNDS: GlobalsBounds = {
  min_claim_idle_minutes: 5,
  max_claim_idle_minutes: 240,
  min_unclaimed_buffer_minutes: 1,
  max_unclaimed_buffer_minutes: 7 * 24 * 60
};

// Mirrors goserver's slug: 32 chars, no leading or trailing '_'.
export const slugify_reason = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+/, '')
    .slice(0, 32)
    .replace(/_+$/, '');

let reason_seq = 0;
const next_reason_id = (): string => {
  reason_seq += 1;
  return `reason-${reason_seq}`;
};

const as_reason = (raw: unknown): SkipReasonRow | null => {
  if (typeof raw === 'string') {
    const label = raw.trim();
    return label ? { code: slugify_reason(label), label } : null;
  }
  if (raw && typeof raw === 'object') {
    const r = raw as { code?: unknown; label?: unknown };
    const label = typeof r.label === 'string' ? r.label.trim() : '';
    const code = typeof r.code === 'string' && r.code ? r.code : slugify_reason(label);
    return label || code ? { code, label: label || code } : null;
  }
  return null;
};

const hhmm = (raw: unknown, fallback: string): string =>
  typeof raw === 'string' && /^\d{2}:\d{2}$/.test(raw) ? raw : fallback;

// null when the server sent no globals (older server): the card is hidden and
// nothing is saved.
export const parse_gen_globals = (raw: unknown): GenGlobals | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const g = raw as Record<string, unknown>;
  const buffers = (g.unclaimed_alert_buffer_minutes || {}) as Record<string, unknown>;
  const num = (v: unknown): NumericInput => (typeof v === 'number' ? v : '');
  return {
    skip_reasons: (Array.isArray(g.skip_reasons) ? g.skip_reasons : [])
      .map(as_reason)
      .filter((r): r is SkipReasonRow => !!r)
      .map((r) => ({ ...r, id: next_reason_id() })),
    unclaimed_alert_buffer_minutes: {
      P0: num(buffers.P0),
      P1: num(buffers.P1),
      P2: num(buffers.P2)
    },
    claim_idle_minutes: num(g.claim_idle_minutes),
    call_window_start: hhmm(g.call_window_start, '08:00'),
    call_window_end: hhmm(g.call_window_end, '21:00'),
    unclaimed_alert_mentions: (Array.isArray(g.unclaimed_alert_mentions)
      ? g.unclaimed_alert_mentions
      : []
    )
      .filter((m): m is string => typeof m === 'string')
      .join(', '),
    unclaimed_alert_channel_id:
      typeof g.unclaimed_alert_channel_id === 'string' ? g.unclaimed_alert_channel_id : '',
    require_outcome: g.require_outcome === true,
    // The server defaults it on, so only an explicit false is off.
    duplicate_account_skip: g.duplicate_account_skip !== false,
    dirty: []
  };
};

const CALL_WINDOW_KEYS: GlobalsKey[] = ['call_window_start', 'call_window_end'];

// One edit to the card, with the key marked dirty. The call window is one
// setting: either end marks both, so the server never gets half a pair.
export const set_globals_field = <K extends GlobalsKey>(
  g: GenGlobals,
  key: K,
  value: GenGlobals[K]
): GenGlobals => {
  const keys = CALL_WINDOW_KEYS.includes(key) ? CALL_WINDOW_KEYS : [key];
  return {
    ...g,
    [key]: value,
    dirty: Array.from(new Set([...g.dirty, ...keys]))
  };
};

// Skip-reason list edits. A new row's code follows its label.
export const edit_reason_label = (
  rows: SkipReasonRow[],
  index: number,
  label: string
): SkipReasonRow[] =>
  rows.map((r, i) => {
    if (i !== index) return r;
    return r.is_new ? { ...r, label, code: slugify_reason(label) } : { ...r, label };
  });

export const add_reason = (rows: SkipReasonRow[]): SkipReasonRow[] => [
  ...rows,
  { code: '', label: '', is_new: true, id: next_reason_id() }
];

// The last row stays: the server restores its defaults on an empty list.
export const remove_reason = (rows: SkipReasonRow[], index: number): SkipReasonRow[] =>
  rows.length <= 1 ? rows : rows.filter((_, i) => i !== index);

export const SLACK_ID_RE = /^[A-Z0-9]{2,32}$/;

export const split_mentions = (raw: string): string[] =>
  raw
    .split(',')
    .map((m) => m.trim())
    .filter(Boolean);

export interface GlobalsErrors {
  call_window?: string;
  // Row indexes whose code repeats an earlier row's.
  duplicate_reasons: number[];
  // Row indexes with no usable label (blank, or nothing to slug a code from).
  empty_reasons: number[];
  // Entries that are not Slack user IDs.
  bad_mentions: string[];
  alert_channel?: string;
}

const HHMM_RE = /^\d{2}:\d{2}$/;

// Inline errors for the dirty keys; any error blocks Save.
export const validate_gen_globals = (g: GenGlobals | null | undefined): GlobalsErrors => {
  const out: GlobalsErrors = { duplicate_reasons: [], empty_reasons: [], bad_mentions: [] };
  if (!g) return out;
  if (g.dirty.includes('call_window_start') || g.dirty.includes('call_window_end')) {
    if (!HHMM_RE.test(g.call_window_start) || !HHMM_RE.test(g.call_window_end)) {
      out.call_window = 'Enter both call window times';
    } else if (g.call_window_start >= g.call_window_end) {
      out.call_window = 'The call window must start before it ends';
    }
  }
  if (g.dirty.includes('skip_reasons')) {
    const seen = new Set<string>();
    g.skip_reasons.forEach((r, i) => {
      const code = r.code || slugify_reason(r.label);
      if (!r.label.trim() || !code) {
        out.empty_reasons.push(i);
        return;
      }
      if (seen.has(code)) out.duplicate_reasons.push(i);
      seen.add(code);
    });
  }
  if (g.dirty.includes('unclaimed_alert_mentions')) {
    out.bad_mentions = split_mentions(g.unclaimed_alert_mentions).filter(
      (m) => !SLACK_ID_RE.test(m)
    );
  }
  const channel = g.unclaimed_alert_channel_id.trim();
  // Empty disables the alert (server falls back to its configured channel, if any).
  if (g.dirty.includes('unclaimed_alert_channel_id') && channel && !SLACK_ID_RE.test(channel)) {
    out.alert_channel = 'Not a Slack channel ID (e.g. C012AB3CD)';
  }
  return out;
};

export const has_globals_errors = (e: GlobalsErrors): boolean =>
  !!e.call_window ||
  !!e.alert_channel ||
  e.duplicate_reasons.length > 0 ||
  e.empty_reasons.length > 0 ||
  e.bad_mentions.length > 0;

export const move_reason = (
  rows: SkipReasonRow[],
  index: number,
  delta: -1 | 1
): SkipReasonRow[] => {
  const to = index + delta;
  if (to < 0 || to >= rows.length) return rows;
  const next = rows.slice();
  [next[index], next[to]] = [next[to], next[index]];
  return next;
};

const clamp_int = (raw: NumericInput, min: number, max: number): number | null => {
  const n = optionalInt(raw);
  if (n === null) return null;
  return Math.min(Math.max(n, min), max);
};

/*
 * The POST body's `config.globals` patch: only the keys the operator changed.
 * The server merges key by key, so {} (nothing loaded, nothing touched) leaves
 * every stored value alone — a save can never wipe the globals. A dirty but
 * blank number is dropped for the same reason.
 */
export const build_gen_globals_payload = (
  g: GenGlobals | null | undefined,
  bounds: GlobalsBounds = DEFAULT_GLOBALS_BOUNDS
): Record<string, unknown> => {
  if (!g) return {};
  const out: Record<string, unknown> = {};
  g.dirty.forEach((key) => {
    switch (key) {
      case 'skip_reasons': {
        const seen = new Set<string>();
        const rows = g.skip_reasons
          .map((r) => ({ label: r.label.trim(), code: r.code || slugify_reason(r.label) }))
          .filter((r) => r.label && r.code && !seen.has(r.code) && !!seen.add(r.code));
        // [] would make the server restore its defaults.
        if (rows.length) out.skip_reasons = rows;
        break;
      }
      case 'unclaimed_alert_buffer_minutes': {
        const b = g.unclaimed_alert_buffer_minutes;
        const lo = bounds.min_unclaimed_buffer_minutes;
        const hi = bounds.max_unclaimed_buffer_minutes;
        const P0 = clamp_int(b.P0, lo, hi);
        const P1 = clamp_int(b.P1, lo, hi);
        const P2 = clamp_int(b.P2, lo, hi);
        if (P0 !== null && P1 !== null && P2 !== null) {
          out.unclaimed_alert_buffer_minutes = { P0, P1, P2 };
        }
        break;
      }
      case 'claim_idle_minutes': {
        const n = clamp_int(
          g.claim_idle_minutes,
          bounds.min_claim_idle_minutes,
          bounds.max_claim_idle_minutes
        );
        if (n !== null) out.claim_idle_minutes = n;
        break;
      }
      case 'call_window_start':
      case 'call_window_end':
        if (HHMM_RE.test(g[key])) out[key] = g[key];
        break;
      case 'unclaimed_alert_mentions':
        out.unclaimed_alert_mentions = Array.from(
          new Set(split_mentions(g.unclaimed_alert_mentions))
        );
        break;
      case 'unclaimed_alert_channel_id': {
        const id = g.unclaimed_alert_channel_id.trim();
        if (!id || SLACK_ID_RE.test(id)) out.unclaimed_alert_channel_id = id;
        break;
      }
      case 'require_outcome':
        out.require_outcome = !!g.require_outcome;
        break;
      case 'duplicate_account_skip':
        out.duplicate_account_skip = !!g.duplicate_account_skip;
        break;
      default:
    }
  });
  return out;
};

export default buildGenConfigPayload;
